'use client'

import React, { useState, useEffect, useCallback } from 'react'
import Dialog from '@mui/material/Dialog'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import Tooltip from '@mui/material/Tooltip'
import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import Slide from '@mui/material/Slide'
import type { TransitionProps } from '@mui/material/transitions'

import { ChatInterface } from './ChatInterface'
import { ChatHistoryDrawer } from './ChatHistoryDrawer'
import { useGetAllSessionsQuery } from '@/core/services/medconnect-agent-service'

const Transition = React.forwardRef(function Transition(props: TransitionProps & { children: React.ReactElement<any, any> }, ref: React.Ref<unknown>) {
  return <Slide direction="up" ref={ref} {...props} />
})

export interface ChatModalProps {
  open: boolean
  onClose: () => void
  sessionId?: string | null
  onSessionIdChange?: (sessionId: string) => void
  titleKey?: string // reuse i18n keys from ChatInterface page where possible
  agentType?: string
  context?: Record<string, unknown> // Additional context to pass to the agent (e.g., expertiseId, patientId)
  onMessageGenerated?: (message: string, messageId: string) => void // Callback when agent generates a message (e.g., for filling form fields)
  initialPrompt?: string // When provided, auto-send this prompt when the modal opens with a new session
}

const SIDEBAR_WIDTH = 280
const SIDEBAR_COLLAPSED_WIDTH = 100

export const ChatModal: React.FC<ChatModalProps> = ({ open, onClose, sessionId, onSessionIdChange, agentType, context, onMessageGenerated, initialPrompt }) => {
  const [activeInitialPrompt, setActiveInitialPrompt] = useState<string | undefined>(initialPrompt)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [drawerOpen, setDrawerOpen] = useState(!isMobile) // Open by default on desktop, closed on mobile
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(false)
  const [selectedSessionId, setSelectedSessionId] = useState<string | null | undefined>(sessionId)

  // Get all sessions for the sidebar
  const { data: sessions, isLoading: isLoadingSessions, error: sessionsError, refetch: refetchSessions } = useGetAllSessionsQuery(undefined)

  // Persist session to localStorage
  const getStorageKey = useCallback(() => {
    const contextKey = context ? JSON.stringify(context) : 'default'
    return `elara-chat-session-${agentType || 'default'}-${contextKey}`
  }, [agentType, context])

  // Restore session from localStorage on mount
  useEffect(() => {
    if (open && !selectedSessionId) {
      const storedSessionId = localStorage.getItem(getStorageKey())
      if (storedSessionId) {
        setSelectedSessionId(storedSessionId)
        onSessionIdChange?.(storedSessionId)
      }
    }
  }, [open, selectedSessionId, getStorageKey, onSessionIdChange])

  // Update selected session when prop changes
  useEffect(() => {
    setSelectedSessionId(sessionId)
  }, [sessionId])

  // Sync activeInitialPrompt when initialPrompt prop changes
  useEffect(() => {
    setActiveInitialPrompt(initialPrompt)
  }, [initialPrompt])

  // Refresh sessions when modal opens
  useEffect(() => {
    if (open) {
      refetchSessions()
    }
  }, [open, refetchSessions])

  // Save session to localStorage when it changes
  const handleSessionIdChange = useCallback(
    (newSessionId: string) => {
      setSelectedSessionId(newSessionId)
      const storageKey = getStorageKey()
      localStorage.setItem(storageKey, newSessionId)
      onSessionIdChange?.(newSessionId)
      refetchSessions() // Refresh sessions list
      // Clear initial prompt after it's been consumed (session was created from it)
      setActiveInitialPrompt(undefined)
    },
    [onSessionIdChange, refetchSessions, getStorageKey]
  )

  const handleSessionSelect = useCallback(
    (sessionId: string) => {
      setSelectedSessionId(sessionId)
      const storageKey = getStorageKey()
      localStorage.setItem(storageKey, sessionId)
      onSessionIdChange?.(sessionId)
      if (isMobile) {
        setDrawerOpen(false)
      }
    },
    [isMobile, onSessionIdChange, getStorageKey]
  )

  const handleNewChat = useCallback(() => {
    setSelectedSessionId(null)
    const storageKey = getStorageKey()
    localStorage.removeItem(storageKey)
    onSessionIdChange?.(null as any)
    if (isMobile) {
      setDrawerOpen(false)
    }
  }, [onSessionIdChange, isMobile, getStorageKey])

  const handleDrawerToggle = useCallback(() => {
    setDrawerOpen((prev) => !prev)
  }, [])

  const handleDrawerCollapseToggle = useCallback(() => {
    setIsDrawerCollapsed((prev) => !prev)
  }, [])

  const tealColor = '#2AB6B7'

  return (
    <Dialog
      fullScreen={isMobile}
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          bgcolor: '#f5f5f5',
          maxWidth: '95vw',
          width: '95vw',
          height: isMobile ? '100vh' : '90vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      maxWidth={false}
    >
      <AppBar
        position="relative"
        elevation={0}
        sx={{
          bgcolor: 'white',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, px: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              bgcolor: tealColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              position: 'relative',
            }}
          >
            {/* Chat bubble icon */}
            <Box
              component="svg"
              sx={{
                width: 20,
                height: 20,
                color: 'white',
                position: 'absolute',
              }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
            </Box>
            {/* Plus sign */}
            <Box
              component="svg"
              sx={{
                width: 12,
                height: 12,
                color: 'white',
                position: 'absolute',
                bottom: 2,
                right: 2,
              }}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </Box>
          </Box>

          {/* Title Section */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="div" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
              Elara Chat
            </Typography>
          </Box>

          {/* Menu and Close Icons */}
          <IconButton edge="end" color="inherit" onClick={handleDrawerToggle} sx={{ mr: 1 }} aria-label="toggle history">
            <MenuIcon />
          </IconButton>
          <IconButton edge="end" color="primary" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          bgcolor: '#f7f7f8',
          position: 'relative',
        }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{
              keepMounted: true, // Better mobile performance
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: SIDEBAR_WIDTH,
                boxSizing: 'border-box',
              },
            }}
          >
            <ChatHistoryDrawer
              variant="sidebar"
              isCollapsed={isDrawerCollapsed}
              onCollapseToggle={handleDrawerCollapseToggle}
              selectedSessionId={selectedSessionId}
              onSessionSelect={handleSessionSelect}
              onNewChat={handleNewChat}
              sessions={sessions}
              isLoadingSessions={isLoadingSessions}
              sessionsError={sessionsError}
            />
          </Drawer>
        ) : (
          drawerOpen && (
            <Box
              sx={{
                width: isDrawerCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
                transition: 'width 0.2s ease',
                borderRight: '1px solid rgba(0,0,0,0.08)',
                bgcolor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <ChatHistoryDrawer
                variant="sidebar"
                isCollapsed={isDrawerCollapsed}
                onCollapseToggle={handleDrawerCollapseToggle}
                selectedSessionId={selectedSessionId}
                onSessionSelect={handleSessionSelect}
                onNewChat={handleNewChat}
                sessions={sessions}
                isLoadingSessions={isLoadingSessions}
                sessionsError={sessionsError}
              />
            </Box>
          )
        )}

        {/* Main Chat Interface */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ChatInterface
            sessionId={selectedSessionId ?? null}
            onSessionIdChange={handleSessionIdChange}
            agentType={agentType}
            context={context}
            onMessageGenerated={onMessageGenerated}
            initialPrompt={activeInitialPrompt}
          />
        </Box>
      </Box>
    </Dialog>
  )
}

export default ChatModal
